import axios from "axios";

// Relative paths keep API requests on the current friendly or development origin.
const api = axios.create({
  baseURL: "/api/v1",
  timeout: 3000,
});

export async function fetchSystemState() {
  try {
    const res = await api.get("/system/state?count=50");
    const payload = res.data || {};
    return {
      system: payload.system || {},
      graph: payload.graph || { nodes: [], edges: [], summary: {} },
      timeline: normalizeEvents(payload.timeline || []),
      metrics: payload.metrics || {},
      ai_summary: payload.ai_summary || {},
    };
  } catch (err) {
    console.error("Failed to fetch system state:", err);
    return {
      system: { services: { total: 0, running: 0, stopped: 0, error: 0 } },
      graph: { nodes: [], edges: [], summary: {} },
      timeline: [],
      metrics: {},
      ai_summary: {},
    };
  }
}

export async function fetchGraph() {
  try {
    const res = await api.get("/system/graph");
    const graph = res.data || {};
    return {
      nodes: graph.nodes || [],
      edges: graph.edges || [],
      summary: graph.summary || {},
    };
  } catch (err) {
    console.error("Failed to fetch graph:", err);
    return { nodes: [], edges: [] };
  }
}

export const fetchEvents = async (count = 50) => {
  try {
    const res = await api.get(`/system/events?count=${encodeURIComponent(count)}`);
    return normalizeEvents(res.data.events || []);
  } catch (err) {
    console.error("Failed to fetch events:", err);
    return [];
  }
};

export const fetchEventSummary = async ({
  count = 50,
  severity = "all",
  category = "all",
  sourceContains = "",
  entityContains = "",
} = {}) => {
  try {
    const params = new URLSearchParams({
      count: String(count),
      severity: String(severity || "all"),
      category: String(category || "all"),
      source_contains: String(sourceContains || ""),
      entity_contains: String(entityContains || ""),
    });
    const res = await api.get(`/system/events/summary?${params.toString()}`);
    return res.data || {};
  } catch (err) {
    console.warn("Failed to fetch event summary:", err.message);
    return {};
  }
};

export async function fetchOverview() {
  try {
    const state = await fetchSystemState();
    return state.system;
  } catch (err) {
    console.error("Failed to fetch overview:", err);
    return { services: { total: 0, running: 0, stopped: 0, error: 0 } };
  }
}

export async function fetchStudioSummary() {
  try {
    const res = await api.get("/display/studio/summary");
    return res.data || null;
  } catch (err) {
    console.warn("Failed to fetch Studio summary:", err.message);
    return null;
  }
}

export async function fetchStudioApplications() {
  try {
    const res = await api.get("/studio/applications");
    if (!res.data || !Array.isArray(res.data.applications)) {
      throw new Error("Studio application directory returned an invalid response.");
    }
    return res.data.applications;
  } catch (err) {
    const message = err.response?.data?.error || "Could not load the Studio application directory.";
    throw new Error(message);
  }
}

export async function fetchStudioSchedulingOptions() {
  try {
    const res = await api.get("/studio/scheduling/options");
    return res.data || {};
  } catch (err) {
    const msg = err.response?.data?.error || "Could not load class scheduling options.";
    throw new Error(msg);
  }
}

export async function fetchPastStudioEvents() {
  try {
    const res = await api.get("/studio/schedule/past");
    return res.data || { events: [], read_only: true };
  } catch (err) {
    const message = err.response?.data?.error || "Could not load past classes.";
    throw new Error(message);
  }
}

export async function fetchNotificationChannels() {
  try {
    const res = await api.get("/notifications/channels");
    const data = res.data;
    if (!data || !Array.isArray(data.channels)
      || (data.public_server_url !== null && typeof data.public_server_url !== "string")
      || (data.test_topic !== null && typeof data.test_topic !== "string")
      || (data.test_subscription_uri !== null && typeof data.test_subscription_uri !== "string")) {
      throw new Error("Notification channels returned an invalid response.");
    }
    return data;
  } catch (err) {
    const message = err.response?.data?.error || "Could not load notification channels.";
    throw new Error(message);
  }
}

export async function fetchNotificationSubscriptions() {
  try {
    const res = await api.get("/notifications/subscriptions");
    if (!res.data || !Array.isArray(res.data.subscriptions)) {
      throw new Error("Notification subscriptions returned an invalid response.");
    }
    return res.data;
  } catch (err) {
    const message = err.response?.data?.error || "Could not load notification subscriptions.";
    throw new Error(message);
  }
}

export async function fetchNotificationAccount() {
  try {
    const res = await api.get("/notifications/account");
    const data = res.data;
    if (!data || !["READY", "FAILED_RETRYABLE", "NOT_CONFIGURED", "IDENTITY_CONFLICT"].includes(String(data.status || "").toUpperCase())) {
      throw new Error("Notification account returned an invalid response.");
    }
    if (typeof data.central_identity_available !== "boolean"
      || (data.central_account_id !== null && typeof data.central_account_id !== "string")
      || (data.provider_identity_ref !== null && typeof data.provider_identity_ref !== "string")) {
      throw new Error("Notification account returned invalid identity details.");
    }
    if (data.central_identity_available
      && (!data.central_account_id || !data.provider_identity_ref)) {
      throw new Error("Notification account is missing its authenticated identity mapping.");
    }
    if (String(data.status).toUpperCase() === "READY"
      && String(data.provider_observed_state || "").toUpperCase() !== "VERIFIED") {
      throw new Error("Notification account READY state is missing provider verification.");
    }
    if (data.account !== null && (!data.account || typeof data.account !== "object")) {
      throw new Error("Notification account returned an invalid response.");
    }
    return data;
  } catch (err) {
    const message = err.response?.data?.error || "Could not load notification account.";
    throw new Error(message);
  }
}

export async function createNotificationAccount(payload) {
  try {
    const res = await api.post("/notifications/account", {
      password: payload?.password,
      confirm_password: payload?.confirm_password,
    });
    if (!res.data || String(res.data.status || "").toUpperCase() !== "READY"
      || String(res.data.provider_observed_state || "").toUpperCase() !== "VERIFIED"
      || !res.data.account) {
      throw new Error("Notification account setup did not return provider-verified READY.");
    }

    return res.data;
  } catch (err) {
    const message = err.response?.data?.error || "Could not set up the ntfy service account.";
    throw new Error(message);
  }
}

export async function sendNotificationTest() {
  try {
    const res = await api.post("/notifications/test", {});
    if (!res.data || String(res.data.status || "").toUpperCase() !== "SENT_TO_NTFY") {
      throw new Error("ntfy did not confirm accepting the test notification.");
    }
    return res.data;
  } catch (err) {
    const message = err.response?.data?.warning || err.response?.data?.error || "Could not send the ntfy test notification.";
    throw new Error(message);
  }
}

export async function createNotificationSubscription(channel) {
  try {
    const res = await api.post("/notifications/subscriptions", { channel });
    if (!res.data || String(res.data.status || "").toUpperCase() !== "ACTIVE" || !res.data.subscription || typeof res.data.uri !== "string") {
      throw new Error("Notification subscription did not return provider-verified ACTIVE.");
    }
    return res.data;
  } catch (err) {
    const message = err.response?.data?.error || "Could not create notification subscription.";
    throw new Error(message);
  }
}

export async function revokeNotificationSubscription(subscriptionId) {
  try {
    const res = await api.delete(`/notifications/subscriptions/${encodeURIComponent(subscriptionId)}`);
    if (!res.data || String(res.data.status || "").toUpperCase() !== "REVOKED" || !res.data.subscription) {
      throw new Error("Notification revoke did not return provider-verified REVOKED.");
    }
    return res.data;
  } catch (err) {
    const message = err.response?.data?.error || "Could not revoke notification subscription.";
    throw new Error(message);
  }
}

export async function createStudioEvent(payload) {
  try {
    const res = await api.post("/studio/events", payload);
    return res.data || {};
  } catch (err) {
    const msg = err.response?.data?.error || "Could not schedule class.";
    throw new Error(msg);
  }
}

export async function fetchCapabilityControlSurface() {
  try {
    const res = await api.get("/studio/capabilities/control-surface");
    return res.data || { capabilities: [] };
  } catch (err) {
    const message = err.response?.data?.error || "Could not load capability controls.";
    throw new Error(message);
  }
}

export async function executeCapabilityAction(capabilityId, action, payload = {}) {
  try {
    const res = await api.post(
      `/studio/capabilities/${encodeURIComponent(capabilityId)}/actions`,
      { ...payload, action },
    );
    return res.data || {};
  } catch (err) {
    const message = err.response?.data?.error || "Could not execute capability action.";
    const error = new Error(message);
    error.result = err.response?.data || null;
    throw error;
  }
}

export async function fetchCurrentUser() {
  try {
    const res = await api.get("/auth/me");
    return res.data || { authenticated: false, user: null };
  } catch (err) {
    console.warn("Failed to fetch current user:", err.message);
    return { authenticated: false, user: null };
  }
}

export async function fetchDeploymentIdentity() {
  try {
    const res = await api.get("/deployment/identity");
    return res.data || { status: "UNVERIFIED" };
  } catch (err) {
    throw new Error(err.response?.data?.error || "Deployment identity is unavailable.");
  }
}

export async function fetchSiteDesignerCurrent() {
  try {
    const [current, schema] = await Promise.all([
      api.get("/operator/site-designer/current"),
      api.get("/operator/site-designer/schema"),
    ]);
    return { ...current.data, schema: schema.data?.schema || {} };
  } catch (err) {
    throw new Error(err.response?.data?.error || "Site Designer is unavailable.");
  }
}

export async function previewSiteDesigner(config, expected_revision) {
  try {
    const res = await api.post("/operator/site-designer/preview", { config, expected_revision });
    return res.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || "Configuration validation failed.");
  }
}

export async function saveSiteDesigner(config, expected_revision) {
  try {
    const res = await api.post("/operator/site-designer/revisions", { config, expected_revision });
    return res.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || "Validated revision could not be saved.");
  }
}

export async function fetchSiteDesignerRevisions() {
  try {
    const res = await api.get("/operator/site-designer/revisions");
    return res.data?.revisions || [];
  } catch (err) {
    throw new Error(err.response?.data?.error || "Saved revisions are unavailable.");
  }
}

export async function rollbackSiteDesigner(revision, expected_revision) {
  try {
    const res = await api.post("/operator/site-designer/rollback", {
      revision, expected_revision, confirmed: true,
    });
    return res.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || "Rollback revision could not be saved.");
  }
}

export async function login(username, password) {
  try {
    const res = await api.post("/auth/login", { username, password });
    return res.data || { success: false };
  } catch (err) {
    const serverMsg = err.response?.data?.error || "Invalid credentials.";
    throw new Error(serverMsg);
  }
}

export async function createPasswordRecoveryRequest(username) {
  try {
    const res = await api.post("/auth/password-recovery-requests", { username }, { timeout: 10000 });
    if (res.data?.status !== "REQUEST_RECEIVED" || typeof res.data.message !== "string") {
      throw new Error("Password recovery request returned an invalid response.");
    }
    return res.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || err.message || "Password recovery is unavailable.");
  }
}

export async function completePasswordRecovery(payload) {
  try {
    const res = await api.post("/auth/password-recovery", {
      username: payload.username,
      token: payload.token,
      new_password: payload.newPassword,
    }, { timeout: 10000 });
    if (res.data?.status !== "COMPLETED" || typeof res.data.message !== "string") {
      throw new Error("Password recovery returned an invalid response.");
    }
    return res.data;
  } catch (err) {
    throw new Error(
      err.response?.data?.error || err.message || "Recovery code or request is invalid or unavailable.",
    );
  }
}

export async function fetchPendingPasswordRecoveryRequests() {
  try {
    const res = await api.get("/iam/password-recovery-requests");
    if (!Array.isArray(res.data?.requests) || !res.data.requests.every((item) =>
      item && typeof item.request_id === "string"
      && typeof item.login === "string" && typeof item.requested_at === "string")) {
      throw new Error("Password recovery requests returned an invalid response.");
    }
    return res.data.requests;
  } catch (err) {
    throw new Error(
      err.response?.data?.error || err.message || "Password recovery requests are unavailable.",
    );
  }
}

export async function decidePasswordRecoveryRequest(requestId, decision) {
  try {
    const res = await api.post(
      `/iam/password-recovery-requests/${encodeURIComponent(requestId)}/decision`,
      { decision },
    );
    const result = res.data;
    if (!result || !["APPROVED", "REJECTED", "EXPIRED"].includes(result.status)
        || (result.status === "APPROVED"
          && (typeof result.token !== "string" || !result.token))) {
      throw new Error("Password recovery decision returned an invalid response.");
    }
    return result;
  } catch (err) {
    throw new Error(
      err.response?.data?.error || err.message || "The recovery decision could not be recorded.",
    );
  }
}

// Public "Join the family" request. Creates a pending request only; it never grants access.
export async function createSignupRequest(payload) {
  try {
    const res = await api.post("/iam/signup-requests", payload, { timeout: 10000 });
    return { ok: true, status: res.status };
  } catch (err) {
    return { ok: false, status: err.response?.status || 0 };
  }
}

export async function fetchPendingSignupRequests() {
  try {
    const res = await api.get("/iam/signup-requests");
    return Array.isArray(res.data?.requests) ? res.data.requests : [];
  } catch (err) {
    throw new Error(err.response?.data?.error || "Pending signups are unavailable.");
  }
}

export async function fetchUserDirectory({ search = "", limit = 20, offset = 0 } = {}) {
  try {
    const params = new URLSearchParams({
      search: String(search),
      limit: String(limit),
      offset: String(offset),
    });
    const response = await api.get(`/users?${params.toString()}`);
    const data = response.data;
    const states = new Set(["ACTIVE", "SUSPENDED", "REVOKED"]);
    if (!data || !Array.isArray(data.users)
        || !Number.isInteger(data.total) || data.total < 0
        || data.limit !== limit || data.offset !== offset
        || !data.users.every((item) => item
          && typeof item.id === "string"
          && typeof item.username === "string"
          && typeof item.display_name === "string"
          && states.has(item.status)
          && (item.version === null || Number.isInteger(item.version) && item.version >= 1)
          && Array.isArray(item.roles)
          && item.roles.every((role) => typeof role === "string")
          && (item.created_at === null || typeof item.created_at === "string"))) {
      throw new Error("Account directory returned an invalid response.");
    }
    return data;
  } catch (err) {
    throw new Error(err.response?.data?.error || err.message || "Account directory is unavailable.");
  }
}

export async function updateAccountStatus(accountId, status, expectedVersion) {
  try {
    const response = await api.post(
      `/iam/users/${encodeURIComponent(accountId)}/status`,
      { status, expected_version: expectedVersion },
    );
    const data = response.data;
    if (!data || data.account_id !== accountId || data.status !== status
        || data.version !== expectedVersion + 1) {
      throw new Error("Account status update returned an invalid response.");
    }
    return data;
  } catch (err) {
    const error = new Error(err.response?.data?.error || err.message || "Account status could not be changed.");
    error.statusCode = err.response?.status || 0;
    throw error;
  }
}

export async function decideSignupRequest(requestId, decision, role = "CLIENT") {
  try {
    const res = await api.post(`/iam/signup-requests/${encodeURIComponent(requestId)}/decision`, { decision, role });
    return res.data || {};
  } catch (err) {
    throw new Error(err.response?.data?.error || "The signup decision could not be recorded.");
  }
}

export async function requestOwnEmailChange(email) {
  try {
    const response = await api.post("/profile/change-requests", { change_type: "EMAIL_CHANGE", email });
    return response.data || {};
  } catch (err) {
    throw new Error(err.response?.data?.error || "The email change request could not be submitted.");
  }
}

export async function fetchPendingProfileChangeRequests() {
  try {
    const response = await api.get("/iam/profile-change-requests");
    if (!Array.isArray(response.data?.requests)) throw new Error("Profile change queue returned an invalid response.");
    return response.data.requests;
  } catch (err) {
    throw new Error(err.response?.data?.error || err.message || "Profile change requests are unavailable.");
  }
}

export async function decideProfileChangeRequest(requestId, decision) {
  try {
    const response = await api.post(
      `/iam/profile-change-requests/${encodeURIComponent(requestId)}/decision`,
      { decision },
    );
    return response.data || {};
  } catch (err) {
    throw new Error(err.response?.data?.error || "The profile change decision could not be recorded.");
  }
}

export async function logout() {
  try {
    const res = await api.post("/auth/logout");
    return res.data || { success: false };
  } catch (err) {
    console.warn("Failed to log out:", err.message);
    return { success: false };
  }
}

export async function fetchOwnProfile() {
  const response = await api.get("/profile");
  return response.data;
}

export async function updateOwnProfile(payload) {
  const response = await api.patch("/profile", payload);
  return response.data;
}

export async function runStudioDoctor() {
  const response = await api.post("/admin/doctor");
  return response.data;
}

export async function fetchOwnPreferences() {
  const response = await api.get("/profile/preferences");
  return response.data;
}

export async function updateOwnPreferences(payload) {
  const response = await api.patch("/profile/preferences", payload);
  return response.data;
}

export async function changeOwnPassword(payload) {
  const response = await api.post("/profile/password", payload);
  return response.data;
}

export async function changeImmichPassword(payload) {
  const response = await api.post("/profile/immich-password", {
    current_password: payload?.current_password,
    new_password: payload?.new_password,
    confirm_password: payload?.confirm_password,
  });
  return response.data;
}

export async function changeJellyfinPassword(payload) {
  const response = await api.post("/profile/jellyfin-password", {
    current_password: payload?.current_password,
    new_password: payload?.new_password,
    confirm_password: payload?.confirm_password,
  });
  return response.data;
}

export async function changeNextcloudPassword(payload) {
  const response = await api.post("/profile/nextcloud-password", {
    current_password: payload?.current_password,
    new_password: payload?.new_password,
    confirm_password: payload?.confirm_password,
  });
  return response.data;
}

export const fetchEntity = async (id) => {
  try {
    const res = await api.get(`/entity/${id}`);
    // API returns:
    // { entity: {...}, recent_events: [...], direct_dependencies: [...], direct_dependents: [...], ... }
    const entity = res.data.entity || res.data.data?.entity || res.data;
    return {
      id: entity.id || id,
      name: entity.name || id,
      type: entity.type || "unknown",
      state: entity.state || "unknown",
      health: entity.health || entity.metadata?.health || "unknown",
      phase: entity.phase || entity.metadata?.phase || "unknown",
      manager: entity.manager || entity.metadata?.manager || "unknown",
      metadata: entity.metadata || {},
      last_updated: entity.last_updated || entity.last_seen || null,
      recent_events: normalizeEvents(res.data.recent_events || []),
      direct_dependencies: res.data.direct_dependencies || [],
      all_dependencies: res.data.all_dependencies || [],
      direct_dependents: res.data.direct_dependents || [],
      all_affected: res.data.all_affected || [],
    };
  } catch (err) {
    console.error(`Failed to fetch entity ${id}:`, err);
    // Return basic entity data from graph if endpoint fails
    return { id, name: id, state: "unknown" };
  }
};

export const fetchEntityImpact = async (id) => {
  try {
    const res = await api.get(`/entity/${id}/impact`);
    return res.data || {};
  } catch (err) {
    console.warn(`Entity impact endpoint not available for ${id}:`, err.message);
    return { entity: id, affected: [] };
  }
};

export const fetchTrace = async (id) => {
  try {
    const res = await api.get(`/trace/${encodeURIComponent(id)}`);
    return {
      id,
      count: res.data?.event_count || 0,
      events: normalizeEvents(res.data?.events || []),
    };
  } catch (err) {
    console.warn(`Trace endpoint unavailable for ${id}:`, err.message);
    return { id, count: 0, events: [] };
  }
};

export const fetchResources = async (range = "15m") => {
  try {
    const res = await api.get(`/system/resources?range=${encodeURIComponent(range)}`);
    return {
      range: res.data?.range || range,
      window_sec: res.data?.window_sec || 900,
      samples: res.data?.samples || [],
      event_marks: res.data?.event_marks || [],
    };
  } catch (err) {
    console.warn("Resource endpoint unavailable:", err.message);
    return { range, window_sec: 900, samples: [], event_marks: [] };
  }
};

export async function studioStartEvent(eventId) {
  try {
    const res = await api.post(`/studio/events/${encodeURIComponent(eventId)}/start`);
    return res.data || {};
  } catch (err) {
    const msg = err.response?.data?.error || 'Could not start class.';
    throw new Error(msg);
  }
}

export async function studioPauseEvent(eventId) {
  try {
    const res = await api.post(`/studio/events/${encodeURIComponent(eventId)}/pause`);
    return res.data || {};
  } catch (err) {
    const msg = err.response?.data?.error || 'Could not pause class.';
    throw new Error(msg);
  }
}

export async function studioResumeEvent(eventId) {
  try {
    const res = await api.post(`/studio/events/${encodeURIComponent(eventId)}/resume`);
    return res.data || {};
  } catch (err) {
    const msg = err.response?.data?.error || 'Could not resume class.';
    throw new Error(msg);
  }
}

export async function studioFinishEvent(eventId) {
  try {
    const res = await api.post(`/studio/events/${encodeURIComponent(eventId)}/finish`);
    return res.data || {};
  } catch (err) {
    const msg = err.response?.data?.error || 'Could not finish class.';
    throw new Error(msg);
  }
}

export async function fetchZoneAmbientOperation(zoneId) {
  try {
    const res = await api.get(`/studio/zones/${encodeURIComponent(zoneId)}/ambient-media`);
    return res.data || {};
  } catch (err) {
    console.warn("Failed to fetch Zone ambient operation:", err.message);
    return null;
  }
}

export async function startZoneAmbientOperation(zoneId) {
  try {
    const res = await api.post(`/studio/zones/${encodeURIComponent(zoneId)}/ambient-media/start`);
    return res.data || {};
  } catch (err) {
    const msg = err.response?.data?.error || "Could not start ambient media.";
    throw new Error(msg);
  }
}

export async function stopZoneAmbientOperation(zoneId) {
  try {
    const res = await api.post(`/studio/zones/${encodeURIComponent(zoneId)}/ambient-media/stop`);
    return res.data || {};
  } catch (err) {
    const msg = err.response?.data?.error || "Could not stop ambient media.";
    throw new Error(msg);
  }
}

export async function studioCheckInClient(eventId, clientId) {
  try {
    const res = await api.post(`/studio/events/${encodeURIComponent(eventId)}/check-in`, { client_id: clientId });
    return res.data || {};
  } catch (err) {
    const msg = err.response?.data?.error || 'Check-in failed.';
    throw new Error(msg);
  }
}

export async function fetchEventTimer(eventId) {
  try {
    const res = await api.get(`/studio/events/${encodeURIComponent(eventId)}/timer`);
    return res.data || {};
  } catch (err) {
    return {};
  }
}

export async function probeCapability(capName) {
  try {
    const res = await api.post(`/studio/capabilities/${encodeURIComponent(capName)}/probe`);
    return res.data || {};
  } catch (err) {
    const msg = err.response?.data?.error || 'Probe failed.';
    throw new Error(msg);
  }
}

export async function recoverCapability(capName) {
  try {
    const res = await api.post(`/studio/capabilities/${encodeURIComponent(capName)}/recover`);
    return res.data || {};
  } catch (err) {
    const msg = err.response?.data?.error || 'Recovery failed.';
    throw new Error(msg);
  }
}

export async function injectCapabilityFailure(capName) {
  try {
    const res = await api.post(`/studio/capabilities/${encodeURIComponent(capName)}/inject-failure`);
    return res.data || {};
  } catch (err) {
    const msg = err.response?.data?.error || 'Injection failed.';
    throw new Error(msg);
  }
}

function normalizeEvents(events) {
  return (events || []).map((item) => {
    const env = item?.envelope || item || {};
    const payload = env.payload || item?.payload || {};
    return {
      ...item,
      envelope: env,
      event: env.event || item?.event || "",
      category: env.category || item?.category || "",
      source: env.source || item?.source || "",
      entity: env.entity || item?.entity || "",
      timestamp: env.timestamp || item?.timestamp || "",
      severity: env.severity || item?.severity || "info",
      trace_id: env.trace_id || item?.trace_id || "",
      correlation_id: env.correlation_id || item?.correlation_id || "",
      payload,
      historical: Boolean(item?.historical ?? payload?.historical ?? false),
      synthetic: Boolean(item?.synthetic ?? payload?.synthetic ?? false),
      stale_by_entity: Boolean(item?.stale_by_entity ?? payload?.stale_by_entity ?? false),
      age_sec: Number(item?.age_sec ?? payload?.age_sec ?? 0),
    };
  });
}
